/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback } from "react";
import { ErrorFactory } from "@core/lib/error-handling";

export type QuotaModalType = "guest_limit" | "plan_limit";

interface QuotaModalContextType {
  isOpen: boolean;
  modalType: QuotaModalType;
  productHint?: string;
  openQuotaModal: (type: QuotaModalType, productHint?: string) => void;
  closeQuotaModal: () => void;
}

const QuotaModalContext = createContext<QuotaModalContextType | undefined>(undefined);

export function QuotaModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalType, setModalType] = useState<QuotaModalType>("guest_limit");
  const [productHint, setProductHint] = useState<string | undefined>(undefined);

  const openQuotaModal = useCallback((type: QuotaModalType, hint?: string) => {
    setModalType(type);
    setProductHint(hint);
    setIsOpen(true);
  }, []);

  const closeQuotaModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <QuotaModalContext.Provider
      value={{
        isOpen,
        modalType,
        productHint,
        openQuotaModal,
        closeQuotaModal,
      }}
    >
      {children}
    </QuotaModalContext.Provider>
  );
}

export function useQuotaModal() {
  const context = useContext(QuotaModalContext);
  if (context === undefined) {
    console.error("🔍 useQuotaModal called outside QuotaModalProvider - context is null");
    console.error("🔍 Stack trace:", new Error().stack);
    // Retourner un contexte par défaut qui correspond au type attendu
    return {
      isOpen: false,
      modalType: "guest_limit" as const,
      productHint: undefined,
      openQuotaModal: () => {
        console.warn("🔍 openQuotaModal called outside QuotaModalProvider - no-op");
      },
      closeQuotaModal: () => {
        console.warn("🔍 closeQuotaModal called outside QuotaModalProvider - no-op");
      },
    };
  }
  return context;
}
